import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import type { BillGetOneResponse } from "@/services/core-api/types"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import { HttpError, httpClient, toHttpError } from "@/utils/api"
import { resolveQueryThrowOnError } from "@/utils/queryClient"
import { useQuery } from "@tanstack/react-query"
import type { UseQueryOptions } from "@tanstack/react-query"

export const isUnavailableBillError = (error: unknown): boolean =>
  error instanceof HttpError && (error.status === 403 || error.status === 404)

interface GetBillParams {
  billId: number
  organizationId: Organization["id"]
}

const getBill = async ({
  billId,
  organizationId,
}: GetBillParams): Promise<BillGetOneResponse> => {
  try {
    const response = await httpClient.get<BillGetOneResponse>(
      `/api/v1/organizations/${organizationId}/bills/${billId}`
    )

    return response.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

interface UseBillOptions {
  billId: number | null
  organizationId: Organization["id"]
  queryOptions?: Omit<
    UseQueryOptions<BillGetOneResponse, HttpError>,
    "queryKey" | "queryFn"
  >
}

export const useBill = ({
  billId,
  organizationId,
  queryOptions = {},
}: UseBillOptions) => {
  const queryKey = useQueryKey(
    ApiQueryName.Bill,
    "getOne",
    billId === null ? null : String(billId),
    {
      organizationId,
    }
  )

  const {
    enabled,
    throwOnError = resolveQueryThrowOnError(
      (error) => !isUnavailableBillError(error)
    ),
    ...restQueryOptions
  } = queryOptions

  const queryResult = useQuery<BillGetOneResponse, HttpError>({
    ...restQueryOptions,
    enabled: Boolean(organizationId) && billId !== null && (enabled ?? true),
    queryFn: async () => {
      if (billId === null) {
        throw new Error("billId is required to fetch bill details")
      }
      return getBill({ billId, organizationId })
    },
    queryKey,
    throwOnError,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.Bill)
}
